import { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Layout } from '../components/layout';
import { Send, CheckCircle2, Loader2, AlertCircle, ArrowRight } from 'lucide-react';

const SITE_URL = 'https://www.patekarecycle.com';

const donateSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  phone: z.string().min(6, 'Please enter a valid phone number'),
  email: z.string().email('Please enter a valid email address'),
  wasteType: z.string().min(1, 'Please select a waste type'),
  address: z.string().min(5, 'Please enter your pickup address'),
  preferredDate: z.string().optional(),
  notes: z.string().optional(),
});

type DonateFormData = z.infer<typeof donateSchema>;

const wasteTypeOptions = [
  'Old Garments & Textiles',
  'Toys & Games',
  'E-waste / Electronics',
  'Books & Stationery',
  'Household Items',
  'Packaging Materials',
  'Other',
];

const donationCategories = [
  { emoji: '👕', label: 'Old Garments & Textiles', desc: 'Clothes, fabric, linen' },
  { emoji: '🧸', label: 'Toys & Games', desc: 'Plastic, electronic, board games' },
  { emoji: '💻', label: 'E-waste & Electronics', desc: 'Phones, laptops, cables' },
  { emoji: '📚', label: 'Books & Stationery', desc: 'Books, notebooks, pens' },
  { emoji: '🔧', label: 'Household Items', desc: 'Utensils, tools, furniture parts' },
  { emoji: '📦', label: 'Packaging Materials', desc: 'Boxes, bubble wrap, foam' },
];

const howItWorks = [
  { num: '01', title: 'Fill the form', desc: 'Tell us what you have and where to pick it up.' },
  { num: '02', title: 'We schedule pickup', desc: 'Our team confirms a convenient time within 24 hours.' },
  { num: '03', title: 'Responsible recycling', desc: 'Materials are sorted and channeled to verified recyclers.' },
];

export function Donate() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const categoriesRef = useRef(null);
  const categoriesInView = useInView(categoriesRef, { once: true, margin: '-60px' });
  const howRef = useRef(null);
  const howInView = useInView(howRef, { once: true, margin: '-60px' });
  const formRef = useRef(null);
  const formInView = useInView(formRef, { once: true, margin: '-60px' });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<DonateFormData>({
    resolver: zodResolver(donateSchema),
  });

  const onSubmit = async (data: DonateFormData) => {
    setIsLoading(true);
    setSubmitError(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      console.log('Donate form submitted:', data);
      setIsSubmitted(true);
      reset();
    } catch {
      setSubmitError('Failed to send. Please email us at info@patekarecycle.com');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Donate Waste | Punarvaa - Give It a Second Life | Patekar Recycle</title>
        <meta name="title" content="Donate Waste | Punarvaa - Give It a Second Life" />
        <meta name="description" content="Donate old garments, toys, e-waste, books and more. Punarvaa ensures responsible recovery and recycling. Schedule a free pickup today." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/donate`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/donate`} />
        <meta property="og:title" content="Donate Waste | Punarvaa - Give It a Second Life" />
        <meta property="og:description" content="Donate old garments, toys, e-waste, books and more. Schedule a free pickup today." />
        <meta property="og:site_name" content="Punarvaa by Patekar Recycle" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Donate Waste | Punarvaa - Give It a Second Life" />
        <meta name="twitter:description" content="Donate old garments, toys, e-waste, books and more. Schedule a free pickup today." />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-p-500 mb-6">
              Community Recycling
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-display text-white leading-[0.95] mb-6">
              Donate Your Waste.<br />
              <span className="text-p-400">Give It a Second Life.</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-xl">
              Old garments, toys, e-waste, books — we ensure responsible recovery and recycling.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What you can donate */}
      <section ref={categoriesRef} className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={categoriesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3">
              Accepted Items
            </p>
            <h2 className="text-3xl font-bold text-gray-900">What you can donate.</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {donationCategories.map((cat, i) => (
              <motion.div
                key={cat.label}
                initial={{ opacity: 0, y: 12 }}
                animate={categoriesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="flex items-start gap-4 p-5 border border-gray-100 rounded-xl hover:border-p-200 hover:bg-p-50 transition-colors"
              >
                <span className="text-3xl">{cat.emoji}</span>
                <div>
                  <p className="font-semibold text-gray-900 mb-0.5">{cat.label}</p>
                  <p className="text-sm text-gray-500">{cat.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section ref={howRef} className="py-20 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={howInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-3">
              Process
            </p>
            <h2 className="text-3xl font-bold text-white">How it works.</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 16 }}
                animate={howInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex gap-5"
              >
                <span className="text-4xl font-bold text-gray-800 tabular-nums shrink-0">{step.num}</span>
                <div>
                  <p className="font-bold text-white mb-1">{step.title}</p>
                  <p className="text-sm text-gray-400 leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation form */}
      <section ref={formRef} className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={formInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-3">
                Schedule Pickup
              </p>
              <h2 className="text-3xl font-bold text-gray-900">Schedule a free pickup.</h2>
            </motion.div>

            {isSubmitted ? (
              <div className="text-center py-16 bg-p-50 rounded-2xl">
                <div className="w-16 h-16 bg-p-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-p-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Pickup Scheduled!</h3>
                <p className="text-gray-500 mb-6">We'll confirm your pickup within 24 hours.</p>
                <button
                  onClick={() => { setIsSubmitted(false); setSubmitError(null); }}
                  className="px-6 py-2.5 bg-p-600 text-white font-medium rounded-lg hover:bg-p-700 transition-colors text-sm"
                >
                  Schedule Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {submitError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm">{submitError}</p>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <input {...register('name')} placeholder="Full Name *"
                      className="w-full px-4 py-3 bg-white border border-gray-200 text-gray-900 placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
                    {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
                  </div>
                  <div>
                    <input {...register('phone')} placeholder="Phone Number *"
                      className="w-full px-4 py-3 bg-white border border-gray-200 text-gray-900 placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
                    {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <input {...register('email')} placeholder="Email Address *"
                      className="w-full px-4 py-3 bg-white border border-gray-200 text-gray-900 placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
                    {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <select {...register('wasteType')}
                      className="w-full px-4 py-3 bg-white border border-gray-200 text-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm">
                      <option value="">Waste Type *</option>
                      {wasteTypeOptions.map((w) => <option key={w} value={w}>{w}</option>)}
                    </select>
                    {errors.wasteType && <p className="mt-1 text-xs text-red-500">{errors.wasteType.message}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <textarea {...register('address')} rows={2} placeholder="Pickup Address *"
                      className="w-full px-4 py-3 bg-white border border-gray-200 text-gray-900 placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent resize-none text-sm" />
                    {errors.address && <p className="mt-1 text-xs text-red-500">{errors.address.message}</p>}
                  </div>
                  <div>
                    <input {...register('preferredDate')} type="date"
                      className="w-full px-4 py-3 bg-white border border-gray-200 text-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent text-sm" />
                    <p className="mt-1 text-xs text-gray-400">Preferred pickup date (optional)</p>
                  </div>
                  <div>
                    <textarea {...register('notes')} rows={2} placeholder="Additional notes (optional)"
                      className="w-full px-4 py-3 bg-white border border-gray-200 text-gray-900 placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-p-500 focus:border-transparent resize-none text-sm" />
                  </div>
                </div>
                <button type="submit" disabled={isLoading}
                  className="w-full py-4 bg-p-600 text-white font-semibold rounded-xl hover:bg-p-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
                  {isLoading ? <><Loader2 className="w-4 h-4 animate-spin" />Scheduling...</> : <><Send className="w-4 h-4" />Schedule Free Pickup</>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Impact note */}
      <section className="py-12 bg-p-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="text-white text-lg font-medium max-w-xl">
              Your donation helps divert waste from landfills and supports local recycling ecosystems.
            </p>
            <a href="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-p-800 font-semibold rounded-xl hover:bg-p-50 transition-colors shrink-0 text-sm">
              Learn More
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
